/*
 * Purpose: Global secrets vault UI — encrypt, unlock, export/import for any project
 * Last modified: 2026-06-25 | Agent
 * Completeness: 92
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Plus,
  Shield,
  Trash2,
  Upload,
} from 'lucide-react';
import { useSecretStore } from '@/stores/secretStore';
import { PRODUCT_BRAND } from '@/lib/productTerms';
import { toast } from 'sonner';

const COMMON_SECRETS = [
  'OPENAI_API_KEY',
  'LINKEDIN_CLIENT_ID',
  'LINKEDIN_CLIENT_SECRET',
  'GOOGLE_CLIENT_ID',
  'GOOGLE_ACCESS_TOKEN',
  'LINKEDIN_TOKEN',
];

export function SecretManager() {
  const {
    secrets,
    isUnlocked,
    hasEncryptedVault,
    legacySecretsPending,
    isSaving,
    createVault,
    unlockVault,
    lockVault,
    setSecret,
    deleteSecret,
    exportVault,
    importVault,
    refreshVaultStatus,
  } = useSecretStore();

  useEffect(() => {
    refreshVaultStatus();
  }, [refreshVaultStatus]);

  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [exportPass, setExportPass] = useState('');
  const [importPass, setImportPass] = useState('');
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const showCreateVault = !hasEncryptedVault;

  const handleCreateVault = async () => {
    try {
      await createVault(passphrase, confirm);
      setPassphrase('');
      setConfirm('');
      toast.success(
        legacySecretsPending ? 'Vault created — legacy secrets migrated' : 'Encrypted vault created'
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create vault');
    }
  };

  const handleUnlock = async () => {
    try {
      await unlockVault(passphrase);
      setPassphrase('');
      toast.success('Vault unlocked — secrets available to all workflows');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Unlock failed');
    }
  };

  const handleAddSecret = async () => {
    if (!name.trim() || !value) {
      toast.error('Name and value required');
      return;
    }
    try {
      await setSecret(name.trim(), value);
      setName('');
      setValue('');
      toast.success(`Saved ${name.trim()} to global vault`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const handleExport = async () => {
    try {
      const json = await exportVault(exportPass || passphrase);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `autoblocks-vault-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Vault exported — import in any ${PRODUCT_BRAND} project`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    }
  };

  const handleImportFile = async (file: File, mode: 'merge' | 'replace') => {
    try {
      const text = await file.text();
      const count = await importVault(text, importPass || passphrase, mode);
      toast.success(`Imported ${count} secret(s) — merged into global vault`);
      setImportPass('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import failed');
    }
  };

  if (!isUnlocked) {
    return (
      <div className="min-h-[min(70vh,32rem)] max-w-lg mx-auto p-4 sm:p-6 space-y-4">
        <Card className="border-primary/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Global secrets vault</CardTitle>
            </div>
            <CardDescription className="text-sm">
              One encrypted vault for every {PRODUCT_BRAND} workflow and compose plan. Export and import
              to reuse keys across projects — never commit vault files to git.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {showCreateVault ? (
              <>
                {legacySecretsPending && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 rounded-md border border-amber-500/30 p-3">
                    Unencrypted secrets found in this browser. Choose a vault passphrase to migrate
                    them to AES-GCM encryption.
                  </p>
                )}
                {!legacySecretsPending && (
                  <p className="text-xs text-muted-foreground rounded-md border border-border p-3">
                    First time here? Create a passphrase-protected vault, then add API keys like{' '}
                    <code className="text-[11px]">OPENAI_API_KEY</code> or{' '}
                    <code className="text-[11px]">LINKEDIN_CLIENT_ID</code>.
                  </p>
                )}
                <div>
                  <Label className="text-xs">Vault passphrase (min 8 chars)</Label>
                  <Input
                    type="password"
                    className="mt-1"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Choose a strong passphrase"
                  />
                </div>
                <div>
                  <Label className="text-xs">Confirm passphrase</Label>
                  <Input
                    type="password"
                    className="mt-1"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Re-enter passphrase"
                  />
                </div>
                <Button className="w-full" onClick={() => void handleCreateVault()} disabled={isSaving}>
                  <KeyRound className="h-4 w-4 mr-2" />
                  {legacySecretsPending ? 'Upgrade & encrypt secrets' : 'Create vault'}
                </Button>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground rounded-md border border-border p-3">
                  Vault already exists in this browser. Enter your passphrase to unlock and manage
                  secrets.
                </p>
                <div>
                  <Label className="text-xs">Vault passphrase</Label>
                  <Input
                    type="password"
                    className="mt-1"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Your vault passphrase"
                  />
                </div>
                <Button className="w-full" onClick={() => void handleUnlock()} disabled={isSaving}>
                  <Lock className="h-4 w-4 mr-2" />
                  Unlock vault
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Global vault
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {secrets.length} secret{secrets.length === 1 ? '' : 's'} · reused by all workflows
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => lockVault()}>
          <Lock className="h-4 w-4 mr-1" />
          Lock
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Add or update secret</CardTitle>
          <CardDescription className="text-xs">
            Same name updates in place. Reference in blocks as <code>$SECRET_NAME</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {COMMON_SECRETS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setName(s)}
                className="text-[10px] px-2 py-1 rounded border hover:bg-muted"
              >
                {s}
              </button>
            ))}
          </div>
          <div>
            <Label htmlFor="secret-name">Name</Label>
            <Input
              id="secret-name"
              placeholder="OPENAI_API_KEY"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 font-mono text-sm"
            />
          </div>
          <div>
            <Label htmlFor="secret-value">Value</Label>
            <Input
              id="secret-value"
              type="password"
              placeholder="Paste key or token…"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1 font-mono text-sm"
            />
          </div>
          <Button onClick={() => void handleAddSecret()} disabled={isSaving} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Save to vault
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {secrets.length === 0 ? (
          <p className="text-muted-foreground text-sm">No secrets yet — add your API keys above.</p>
        ) : (
          secrets.map((secret) => (
            <Card key={secret.id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium font-mono text-sm">{secret.name}</p>
                  <Badge variant="outline" className="text-[10px]">
                    global
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded truncate max-w-[240px]">
                    {showValues[secret.id] ? secret.value : '••••••••••••'}
                  </code>
                  <button
                    type="button"
                    onClick={() => setShowValues((p) => ({ ...p, [secret.id]: !p[secret.id] }))}
                    className="p-1 hover:bg-muted rounded shrink-0"
                  >
                    {showValues[secret.id] ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void deleteSecret(secret.id).then(() => toast.success('Deleted'))}
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </Card>
          ))
        )}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Reuse across projects</CardTitle>
          <CardDescription className="text-xs">
            Export an encrypted file from this project; import it in another {PRODUCT_BRAND} instance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Export / import passphrase (can match vault passphrase)</Label>
            <Input
              type="password"
              className="mt-1"
              placeholder="Passphrase for file encryption"
              value={exportPass}
              onChange={(e) => {
                setExportPass(e.target.value);
                setImportPass(e.target.value);
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => void handleExport()}>
              <Download className="h-4 w-4 mr-1" />
              Export vault
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-1" />
              Import (merge)
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleImportFile(f, 'merge');
                e.target.value = '';
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
